import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Menu,
  X,
  ArrowRight,
} from "lucide-react";
import Logo from "../../assests/Poeage_Logo_1.png";

const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { label: "Web Development", href: "/web" },
      { label: "App Development", href: "/app" },
      { label: "AI Development", href: "/ai" },
      { label: "Digital Marketing", href: "/digi" },
      { label: "Cloud Solutions", href: "/cloud" },
    ],
  },
  { label: "Work", href: "/work" },
  { label: "About", href: "/aboutus" },
];

export default function Header({ setShowQuoteModal }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-gray-200 shadow-sm"
          : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">

        {/* 🔥 LOGO */}
        <Link to="/" className="flex items-center">
          <img
            src={Logo}
            alt="Poeage"
            className="h-9 object-contain hover:scale-105 transition"
          />
        </Link>

        {/* 💎 DESKTOP NAV */}
        <nav className="hidden md:flex items-center gap-2">
          {NAV_LINKS.map((nav) =>
            nav.dropdown ? (
              <div
                key={nav.label}
                className="relative"
                onMouseEnter={() => setActiveDropdown(nav.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-600 hover:text-black transition">
                  {nav.label}
                  <ChevronDown
                    size={14}
                    className={`transition ${
                      activeDropdown === nav.label ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Dropdown */}
                <AnimatePresence>
                  {activeDropdown === nav.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg p-2"
                    >
                      {nav.dropdown.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="block px-4 py-2 text-sm rounded-lg hover:bg-gray-100 transition"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={nav.label}
                to={nav.href}
                className={`px-4 py-2 text-sm font-medium transition ${
                  location.pathname === nav.href
                    ? "text-black"
                    : "text-gray-600 hover:text-black"
                }`}
              >
                {nav.label}
              </Link>
            )
          )}
        </nav>

        {/* 🚀 CTA */}
        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={() => setShowQuoteModal && setShowQuoteModal(true)}
            className="flex items-center gap-2 px-5 py-2 bg-black text-white rounded-full text-sm font-semibold hover:scale-105 active:scale-95 transition"
          >
            Start Project <ArrowRight size={14} />
          </button>
        </div>

        {/* 📱 MOBILE BUTTON */}
        <button
          className="md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* 📱 MOBILE MENU */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-200 px-6 py-6"
          >
            {NAV_LINKS.map((nav) =>
              nav.dropdown ? (
                <div key={nav.label}>
                  <p className="text-xs text-gray-400 uppercase mb-2">
                    {nav.label}
                  </p>
                  {nav.dropdown.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block py-2 text-base font-medium"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={nav.label}
                  to={nav.href}
                  className="block py-3 text-base font-medium"
                >
                  {nav.label}
                </Link>
              )
            )}

            <button
              onClick={() => setShowQuoteModal && setShowQuoteModal(true)}
              className="mt-6 w-full py-3 bg-black text-white rounded-xl font-semibold"
            >
              Start Project
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}